import React, { useCallback, useState, ReactElement } from 'react';
import Card from '../../../components/card/card.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import Loader from '../../../components/loader/loader.component';
import Button from '../../../components/button/button.component';
import ActionButton from '../../../components/action-button/action-button.component';
import ErrorBlock from '../../../components/error-block/error-block.component';
import ModalLabel from '../../../components/modal-label/modal-label.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import ModalFieldBlock from '../../../components/modal-field-block/modal-field-block.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import FieldText from '../../../components/field-text/field-text.component';
import SendIcon from '../../../icons/send-icon';
import FormatService from '../../../services/format-service';
import FormService from '../../../services/form-service';

const DealFileChatModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const MODAL_TITLE_PREFIX: string = 'File Chat';

  const {
    isOpen,
    isResponseLoading,
    fileData,
    responseText,
    errorMessage,
    onCancel,
    onCloseModal,
    onSendQuery,
  } = props;

  const [query, setQuery]: [string, Function] = useState('');

  const fileId: number | null = (fileData && fileData.id) || null;
  const entityId: number | null = (fileData && fileData.entityId) || null;
  const dealFileName: string = (fileData && fileData.file && fileData.file.name) || null;
  const modalTitle: string = dealFileName ? `${MODAL_TITLE_PREFIX}: ${dealFileName}` : MODAL_TITLE_PREFIX;

  const closeModal: Function = useCallback((): void => {
    setQuery('');

    if (onCancel) {
      onCancel();
    }

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const queryFieldChange: Function = useCallback((queryValue: string): void => {
    setQuery(queryValue);
  }, []);

  const sendQuery: Function = useCallback((): void => {
    if (onSendQuery && fileId && query && query !== '') {
      onSendQuery(fileId, query, entityId);
    }
  }, [fileId, query, entityId]);

  return (
    <ModalLayout
      isOpen={isOpen}
      title={modalTitle}
      onCloseModal={closeModal}
      onCloseModalByClickAway={closeModal}
    >
      <Card isContainer>
        <ModalLabel>
          Your question:
        </ModalLabel>
        <ModalFieldBlock>
          <FlexContainer isFullWidth>
            <FieldText
              isPlain
              isBordered
              isFullWidth
              isMultiline
              withAutoFocus
              minRows={FormService.TEXTAREA_MIN_ROWS_MEDIUM}
              maxRows={FormService.TEXTAREA_MAX_ROWS_MEDIUM}
              maxLength={FormatService.STRING_LIMIT_DEFAULT}
              value={query}
              onChange={(event: React.ChangeEvent, value: string): void => {
                queryFieldChange(value);
              }}
            />
          </FlexContainer>
          <FlexContainer>
            {
              !isResponseLoading && (
                <Tooltip title="Send Question">
                  <ActionButton withOffset withSmallPadding isDisabled={isResponseLoading} onClick={sendQuery}>
                    <SendIcon />
                  </ActionButton>
                </Tooltip>
              )
            }
            {
              isResponseLoading && (
                <Loader isInline isSmall />
              )
            }
          </FlexContainer>
        </ModalFieldBlock>
        <ModalLabel>
          Analyzer service response:
        </ModalLabel>
        <ModalFieldBlock>
          <FieldText
            isPlain
            isDisabled
            isBordered
            isFullWidth
            isMultiline
            minRows={FormService.TEXTAREA_MIN_ROWS_MEDIUM}
            maxRows={FormService.TEXTAREA_MAX_ROWS_MEDIUM}
            value={responseText || ''}
          />
        </ModalFieldBlock>
      </Card>
      <ModalButtonBlock>
        <Button onClick={closeModal}>
          Cancel
        </Button>
      </ModalButtonBlock>
      {
        !!errorMessage && (
          <ErrorBlock errorMessage={errorMessage} />
        )
      }
    </ModalLayout>
  );
});

export default DealFileChatModal;
